import _ from "lodash";
import iconImage from "../assets/icon/collections-24px.svg";
import iconPdf from "../assets/icon/picture_as_pdf-24px.svg";
import iconAttachment from "../assets/icon/attach_file-24px.svg";
import { MediaObject } from "../types/MediaObject";
import { isLitigationCase } from "../types/AbstractCase";
import { BackofficeUser } from "../types/BackofficeUser";
import { STAGE_ACTION_REQUIRED, STAGE_DATA_COMPLETED } from "./Stages/StagesCase";
import { ProductUnion } from "../types/ProductUnion";
import dayjs from "dayjs";
import { MediaObjectFolder } from "../types/MediaObjectFolder.ts";
import { HydraCollectionResult } from "../types/HydraCollectionResult";
import { apiGet } from "./Api/apiCall";

export const FILE_SIZE_LIMIT_EXCEEDED = 20_971_520;
export const FILE_NAME_LENGTH_LIMIT = 70;
export const NO_LABEL_ACTION_IDENTIFIERS = ["NO_ACTION", "OPS_ACTIVITY", "LAWYER_ACTIVITY"];

export const scannedMediaObjectsFilters = {
  scanCheck: "required",
  "order[created]": "asc",
  "order[id]": "asc",
};

export const fetchScannedMediaObjects = async (signal: AbortSignal): Promise<MediaObject[]> => {
  const mediaObjects: MediaObject[] = [];
  let page = 1;
  // The inbox contains a few hundred documents. Load every page before filtering;
  // a substantially larger inbox would need server-side filtering and pagination.
  while (true) {
    const collection = await apiGet<HydraCollectionResult<MediaObject>>(
      "media_objects",
      null,
      { ...scannedMediaObjectsFilters, page },
      { signal },
    );
    mediaObjects.push(...collection["hydra:member"]);
    if (collection["hydra:member"].length === 0 || mediaObjects.length >= collection["hydra:totalItems"]) {
      return mediaObjects;
    }
    page += 1;
  }
};

const MAX_UPLOAD_SIZE_IN_MEGABYTES_BY_MIME_TYPE: Record<string, number> = {
  "application/pdf": 25,
  "image/jpeg": 15,
  "image/png": 15,
  "video/x-msvideo": 500,
  "video/mp4": 500,
  "video/mpeg": 500,
  "video/webm": 500,
  "audio/mpeg": 500,
  "audio/ogg": 500,
};

export const getMaxUploadSizeInMegabytes = (file: Pick<File, "name" | "type">) => {
  const mimeTypeLimit = MAX_UPLOAD_SIZE_IN_MEGABYTES_BY_MIME_TYPE[file.type];
  if (mimeTypeLimit) {
    return mimeTypeLimit;
  }

  if (/\.pdf$/i.test(file.name)) {
    return 25;
  }
  if (/\.(jpg|jpeg|png)$/i.test(file.name)) {
    return 15;
  }
  if (/\.(avi|mpg|mpeg|webm|ogg|opus|mp3|mp4)$/i.test(file.name)) {
    return 500;
  }

  // File types not covered by the ticket retain the existing Backoffice limit.
  return FILE_SIZE_LIMIT_EXCEEDED / 1024 / 1024;
};

export const isUploadSizeLimitExceeded = (file: Pick<File, "name" | "size" | "type">) => {
  return file.size > getMaxUploadSizeInMegabytes(file) * 1024 * 1024;
};

export const filterByFolder = (mediaObjects: MediaObject[], folder: string) => {
  return _.filter(mediaObjects, (mediaObject) => {
    return !_.startsWith(mediaObject.electronicFileFolderPath ?? "", folder);
  });
};

export const getIcon = (mediaObject: MediaObject) => {
  switch (mediaObject.mimeType) {
    case "image/jpg":
    case "image/jpeg":
    case "image/png":
      return iconImage;
    case "application/pdf":
      return iconPdf;
    default:
      return iconAttachment;
  }
};

export const getMediaObjectFilters = (
  product: Pick<ProductUnion, "productClassName" | "id">,
  currentUser: BackofficeUser,
) => {
  let mediaObjectsFilter: Partial<MediaObject> = {
    product: product.productClassName,
    productId: product.id,
  };

  if (currentUser.isExternal) {
    mediaObjectsFilter.showInExternalView = true;
  }
  return mediaObjectsFilter;
};

export const getRenderValue = (mediaObjectIds: number[], mediaObjects: MediaObject[]) => {
  return _.map(
    mediaObjectIds,
    (mediaObjectId) => _.find(mediaObjects, (mediaObject) => mediaObject.id === mediaObjectId)?.description,
  ).join(", ");
};

export const getMediaObjectFormattedDescription = (mediaObject: MediaObject) => {
  return mediaObject.description + " | " + dayjs(mediaObject.created).format("DD.MM.YYYY") || "Ohne Beschreibung";
};

export const isValidUploadableFile = (fileName: string, checkLength: boolean = true) => {
  return !(
    /[ ‚{}()%&@#$~!^?*<>,\\+:=/“;[\]|]/.test(fileName) ||
    (fileName.match(/\./g) || []).length > 1 ||
    (checkLength && fileName.length > FILE_NAME_LENGTH_LIMIT)
  );
};

export const getChangeFieldNameModalOptions = (product: ProductUnion, mediaObject: MediaObject) => {
  let fieldNameOptions = [];

  if (
    product &&
    isLitigationCase(product) &&
    product.stage >= STAGE_DATA_COMPLETED &&
    product.stage <= STAGE_ACTION_REQUIRED
  ) {
    if (mediaObject.fieldname === "extrajudicialLetter") {
      fieldNameOptions.push({ id: "removedViaChangeFieldNameModal", label: "Keine Kennzeichnung" });
    } else {
      fieldNameOptions.push({ id: "extrajudicialLetter", label: "Außergerichtliches Schreiben" });
    }
  }

  return fieldNameOptions;
};

export function isMediaObjectFolder(node: MediaObjectFolder | MediaObject): node is MediaObjectFolder {
  return "type" in node && node.type === "folder";
}

export const getSortedMediaObjectFolders = (mediaObjects: MediaObject[]): Array<MediaObjectFolder | MediaObject> => {
  let fileStructure: Array<MediaObjectFolder | MediaObject> = [
    {
      name: "Mandantschaft",
      type: "folder",
      children: [],
    },
    {
      name: "Gerichtliches Verfahren",
      type: "folder",
      children: [],
    },
    {
      name: "Außergerichtliches Verfahren",
      type: "folder",
      children: [],
    },
    {
      name: "RSV",
      type: "folder",
      children: [],
    },
    {
      name: "ToDo",
      type: "folder",
      children: [],
    },
    {
      name: "Rechnungen",
      type: "folder",
      children: [],
    },
    {
      name: "Sonstiges",
      type: "folder",
      children: [],
    },
  ];

  mediaObjects.forEach((mediaObject) => {
    let folderPath = mediaObject.electronicFileFolderPath;
    if (!folderPath) {
      return;
    }

    let folderInFileStructure = fileStructure.find((node) => {
      if (isMediaObjectFolder(node)) {
        return node.name === folderPath;
      }
      return false;
    });

    if (folderInFileStructure && isMediaObjectFolder(folderInFileStructure)) {
      folderInFileStructure.children.push(mediaObject);
    } else {
      fileStructure.push({
        name: folderPath,
        type: "folder",
        children: [mediaObject],
      });
    }
  });

  return fileStructure;
};
